import React from 'react';
import { RefreshCw, PlusCircle,ShoppingBag } from 'react-feather'; // Import icons
const formatUSD = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value || 0);

const CreditBalance = ({
  creditBalance,
  onPurchaseClick,
  onAutoReloadClick,
  autoReloadEnabled,
  autoReloadThreshold,
  autoReloadAmount,
}) => {
  // Format currency
  const formattedBalance = formatUSD(creditBalance);

  // Auto-reload is only *known* once the settings request resolves; undefined
  // means "not loaded yet", which is different from "off". Showing "Off" for
  // an account that actually has it on would be a lie during the load.
  const autoReloadKnown =
    autoReloadEnabled !== undefined && autoReloadEnabled !== null;
  const hasReloadRule =
    Number.isFinite(autoReloadThreshold) && Number.isFinite(autoReloadAmount);

  // With auto-reload on, the balance has a floor: it tops up before hitting
  // zero. That is the single most useful thing to say about a credit balance,
  // so it gets stated in full rather than as a bare "On".
  // When the rule has its own row below, this note must not restate it,
  // otherwise the same "adds $X below $Y" sentence appears twice in one panel.
  // It says what auto-reload *means* instead; the rule row says what it does.
  const autoReloadDetail = !autoReloadKnown
    ? null
    : autoReloadEnabled
      ? hasReloadRule
        ? 'Your balance tops up automatically, so service never interrupts.'
        : 'Tops up automatically before your balance runs out.'
      : 'Service stops when the balance reaches zero.';

  // Headroom meter: how much of the balance sits above the auto-reload
  // threshold, i.e. what is spendable before a top-up is triggered. This is
  // the one thing a balance figure alone cannot say, and it is derived
  // entirely from values we already have rather than invented.
  //
  // Only meaningful with a reload rule on file: without a threshold there is
  // no floor to measure against, and a bar with an arbitrary maximum would be
  // decoration rather than information.
  const showHeadroom =
    autoReloadKnown && autoReloadEnabled && hasReloadRule && autoReloadThreshold > 0;

  // The track has to span from zero to something meaningfully above the
  // current balance, or the fill pins to 100% and the threshold marker is
  // crushed against the left edge, which is exactly what a balance of $83
  // against a $2 top-up point does to a threshold+amount scale.
  //
  // Using the balance itself as the ceiling (with headroom above it) keeps
  // both the fill and the marker in view at any ratio: the marker's position
  // then honestly shows how far the balance is from triggering a top-up.
  const headroomCeiling = Math.max(
    creditBalance * 1.25,
    autoReloadThreshold + autoReloadAmount
  );
  const headroomPct = showHeadroom
    ? Math.max(0, Math.min(100, (creditBalance / headroomCeiling) * 100))
    : 0;
  const thresholdPct = showHeadroom
    ? Math.max(0, Math.min(100, (autoReloadThreshold / headroomCeiling) * 100))
    : 0;

  // Above the floor is normal; at or below it a top-up is imminent, which is
  // worth flagging quietly rather than as an error, auto-reload will handle it.
  const isNearFloor = showHeadroom && creditBalance <= autoReloadThreshold;

  // Step the font down as the balance gets longer so large sums stay on one
  // line. The card clips overflow, so an unsized long balance gets cut off.
  // Thresholds: "$1,001,025.13" = 13, "$99,999,999.99" = 14, "$1,234,567,890.12" = 17.
  const balanceSize =
    formattedBalance.length > 16 ? 'xs'
      : formattedBalance.length > 14 ? 'sm'
        : formattedBalance.length > 12 ? 'md'
          : 'lg';

  return (
    <div className="credit-balance-card">
      <div className="balance-header">
        <h3>
          <span className="balance-header-icon" aria-hidden="true">
            <ShoppingBag size={16} />
          </span>
          Credit Balance
        </h3>
      </div>

      <div className="balance-overview">
        {/* Amount and its label only. The auto-reload state lives on the
            Auto-Reload button's badge, so a second copy here was redundant. */}
        {/* The headline names itself. Repeating the same dollar figure on a
            "Purchased credit" row below would read as a second pot of money,
            the one thing this card exists to rule out. */}
        <div className="balance-amount">
          <h1 data-size={balanceSize} title={formattedBalance}>{formattedBalance}</h1>
          <span className="balance-label">
            Purchased credit
            <span className="balance-label-note">never expires</span>
          </span>
        </div>

        {/* Spendable headroom before auto-reload fires. The marker on the
            track is the top-up threshold, so the bar answers "how close am I
            to a charge?" rather than being a decorative progress bar. */}
        {showHeadroom && (
          <div className={`balance-meter ${isNearFloor ? 'is-near-floor' : ''}`}>
            <div
              className="balance-meter-track"
              role="img"
              aria-label={`Balance ${formattedBalance}. Auto-reload tops up at ${formatUSD(autoReloadThreshold)}.`}
            >
              <div
                className="balance-meter-fill"
                style={{ width: `${headroomPct}%` }}
              />
              <span
                className="balance-meter-marker"
                style={{ left: `${thresholdPct}%` }}
                aria-hidden="true"
              />
            </div>
            <div className="balance-meter-legend">
              <span className="balance-meter-caption">
                {isNearFloor
                  ? `At or below the ${formatUSD(autoReloadThreshold)} top-up point`
                  : `${formatUSD(Math.max(0, creditBalance - autoReloadThreshold))} before top-up`}
              </span>
              <span className="balance-meter-scale">
                Tops up at {formatUSD(autoReloadThreshold)}
              </span>
            </div>
          </div>
        )}

        {/* Facts about the account that the headline number cannot carry, in
            their own quiet panel: the auto-reload rule and its state, what
            happens as the balance runs down.

            The plan fee is deliberately NOT one of them. It is a different
            kind of money (a recurring charge for access, never spendable) and
            the Current Plan section directly below this row already states it
            with its own price and cadence. Two copies on one screen invites
            them to drift, and the reader has to work out whether they are the
            same charge. */}
        <div className="balance-facts">
          {/* Top-up rule as its own fact: what a reload costs and when it
              fires. The auto-reload row below states whether it is on; this
              states what it will do, which the meter above visualises. */}
          {autoReloadKnown && autoReloadEnabled && hasReloadRule && (
            <div className="balance-fact">
              <span className="balance-fact-label">Top-up rule</span>
              <span className="balance-fact-value">
                {formatUSD(autoReloadAmount)}
                <span className="balance-fact-suffix">
                  below {formatUSD(autoReloadThreshold)}
                </span>
              </span>
              <span className="balance-fact-note">
                Charged to your default card on file.
              </span>
            </div>
          )}

          {/* Always rendered once known: "Off" is as much a fact about the
              account as "On", and with auto-reload off there is no top-up rule
              row either, so this is what keeps the panel from being empty. */}
          {autoReloadKnown && (
            <div className="balance-fact">
              <span className="balance-fact-label">Auto-reload</span>
              <span
                className={`balance-fact-state ${autoReloadEnabled ? 'is-on' : 'is-off'}`}
              >
                {autoReloadEnabled ? 'On' : 'Off'}
              </span>
              <span className="balance-fact-note">{autoReloadDetail}</span>
            </div>
          )}
        </div>

        <p className="balance-description">
          Credits are used for AI assistant interactions and other usage-based
          services.
        </p>
      </div>

      <div className="card-actions">
        {/* Existing Add Funds Button */}
        <button className="purchase-credits-btn" onClick={onPurchaseClick}>
          <PlusCircle size={16} />
          Add Funds
        </button>
        {/* The On/Off state lives in the facts panel above, stated with its
            threshold and amount, so the button is just the action. Repeating
            the status pill here made the same fact appear twice in one card. */}
        <button className="auto-reload-btn" onClick={onAutoReloadClick}>
          <RefreshCw size={16} />
          {autoReloadKnown && !autoReloadEnabled ? 'Set up Auto-Reload' : 'Auto-Reload'}
        </button>
      </div>
    </div>
  );
};

export default CreditBalance;
